'use client';

import * as React from 'react';
import PropTypes from 'prop-types';
import { useComponentRenderer } from '../../utils/useComponentRenderer.js';
import { mergeProps } from '../../merge-props/index.js';
import { useBaseUiId } from '../../utils/useBaseUiId.js';
import { useSelectGroupContext } from '../group/SelectGroupContext.js';
import { useEnhancedEffect } from '../../utils/useEnhancedEffect.js';
const state = {};

/**
 * An accessible label that is automatically associated with its parent group.
 * Renders a `<div>` element.
 *
 * Documentation: [Base UI Select](https://base-ui.com/react/components/select)
 */
const SelectGroupLabel = /*#__PURE__*/React.forwardRef(function SelectGroupLabel(props, forwardedRef) {
  const {
    className,
    render,
    id: idProp,
    ...otherProps
  } = props;
  const {
    setLabelId
  } = useSelectGroupContext();
  const id = useBaseUiId(idProp);
  useEnhancedEffect(() => {
    setLabelId(id);
  }, [id, setLabelId]);
  const getSelectItemGroupLabelProps = React.useCallback((externalProps = {}) => mergeProps({
    id
  }, externalProps), [id]);
  const {
    renderElement
  } = useComponentRenderer({
    propGetter: getSelectItemGroupLabelProps,
    render: render ?? 'div',
    ref: forwardedRef,
    state,
    className,
    extraProps: otherProps
  });
  return renderElement();
});
process.env.NODE_ENV !== "production" ? SelectGroupLabel.propTypes /* remove-proptypes */ = {
  // ┌────────────────────────────── Warning ──────────────────────────────┐
  // │ These PropTypes are generated from the TypeScript type definitions. │
  // │ To update them, edit the TypeScript types and run `pnpm proptypes`. │
  // └─────────────────────────────────────────────────────────────────────┘
  /**
   * @ignore
   */
  children: PropTypes.node,
  /**
   * CSS class applied to the element, or a function that
   * returns a class based on the component’s state.
   */
  className: PropTypes.oneOfType([PropTypes.func, PropTypes.string]),
  /**
   * @ignore
   */
  id: PropTypes.string,
  /**
   * Allows you to replace the component’s HTML element
   * with a different tag, or compose it with another component.
   *
   * Accepts a `ReactElement` or a function that returns the element to render.
   */
  render: PropTypes.oneOfType([PropTypes.element, PropTypes.func])
} : void 0;
export { SelectGroupLabel };